import { useEffect, useState } from 'react';

import {
  fetchFeaturedNews,
  fetchNews,
  fetchNewsArticle,
  fetchNewsCategories,
  fetchRelatedNews,
} from '../services/api';
import {
  NewsArticle,
  NewsCategory,
  NewsListResponse,
} from '../services/types/news';

export interface UseNewsReturn {
  data: NewsListResponse;
  loading: boolean;
  error: string | null;
  refetch: (params?: {
    page?: number;
    pageSize?: number;
    category?: string;
    featured?: boolean;
  }) => Promise<void>;
}

/**
 * Hook for fetching news articles with pagination and filtering
 * Enhanced with better error handling and debugging
 */
export function useNews(params?: {
  page?: number;
  pageSize?: number;
  category?: string;
  featured?: boolean;
}): UseNewsReturn {
  const [data, setData] = useState<NewsListResponse>({
    data: [],
    meta: {
      pagination: {
        page: 1,
        pageSize: 9,
        pageCount: 0,
        total: 0,
      },
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (fetchParams?: {
    page?: number;
    pageSize?: number;
    category?: string;
    featured?: boolean;
  }) => {
    try {
      setLoading(true);
      setError(null);

      console.log('useNews: Fetching with params:', fetchParams || params);
      const result = await fetchNews(fetchParams || params);

      console.log('useNews: Received data:', result);
      setData(result);
    } catch (err) {
      console.error('useNews: Error fetching news:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch news';
      setError(errorMessage);

      // Set empty data on error
      setData({
        data: [],
        meta: {
          pagination: {
            page: 1,
            pageSize: 9,
            pageCount: 0,
            total: 0,
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useNews: Effect triggered with params:', params);
    fetchData(params);
  }, [params?.page, params?.pageSize, params?.category, params?.featured]);

  const refetch = async (refetchParams?: {
    page?: number;
    pageSize?: number;
    category?: string;
    featured?: boolean;
  }) => {
    console.log('useNews: Refetching with params:', refetchParams);
    await fetchData(refetchParams);
  };

  return {
    data,
    loading,
    error,
    refetch,
  };
}

export interface UseNewsCategoriesReturn {
  categories: NewsCategory[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching news categories
 * Enhanced with better error handling and debugging
 */
export function useNewsCategories(): UseNewsCategoriesReturn {
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('useNewsCategories: Fetching categories');
      const result = await fetchNewsCategories();

      console.log('useNewsCategories: Received categories:', result);
      setCategories(result);
    } catch (err) {
      console.error('useNewsCategories: Error fetching categories:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch news categories';
      setError(errorMessage);

      // Set empty array on error
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useNewsCategories: Effect triggered');
    fetchData();
  }, []);

  const refetch = async () => {
    console.log('useNewsCategories: Refetching');
    await fetchData();
  };

  return {
    categories,
    loading,
    error,
    refetch,
  };
}

export interface UseNewsArticleReturn {
  article: NewsArticle | null;
  sameCategoryArticles: NewsArticle[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching a single news article and related articles
 * Enhanced with better error handling and debugging
 */
export function useNewsArticle(slug: string): UseNewsArticleReturn {
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [sameCategoryArticles, setSameCategoryArticles] = useState<
    NewsArticle[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('useNewsArticle: Fetching article with slug:', slug);
      const articleResult = await fetchNewsArticle(slug);

      if (articleResult) {
        console.log('useNewsArticle: Article found:', articleResult);
        setArticle(articleResult);

        // Fetch articles from the same category
        console.log('useNewsArticle: Fetching articles from same category');
        const sameCategoryResult = await fetchRelatedNews(articleResult, 3);
        console.log(
          'useNewsArticle: Same category articles:',
          sameCategoryResult
        );
        setSameCategoryArticles(sameCategoryResult);
      } else {
        console.log('useNewsArticle: Article not found');
        setArticle(null);
        setSameCategoryArticles([]);
      }
    } catch (err) {
      console.error('useNewsArticle: Error fetching article:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch news article';
      setError(errorMessage);

      // Reset state on error
      setArticle(null);
      setSameCategoryArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      console.log('useNewsArticle: Effect triggered with slug:', slug);
      fetchData();
    } else {
      console.log('useNewsArticle: No slug provided');
      setLoading(false);
    }
  }, [slug]);

  const refetch = async () => {
    console.log('useNewsArticle: Refetching');
    await fetchData();
  };

  return {
    article,
    sameCategoryArticles,
    loading,
    error,
    refetch,
  };
}

export interface UseFeaturedNewsReturn {
  articles: NewsArticle[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching featured news articles
 * Enhanced with better error handling and debugging
 */
export function useFeaturedNews(): UseFeaturedNewsReturn {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('useFeaturedNews: Fetching featured articles');
      const result = await fetchFeaturedNews();

      console.log('useFeaturedNews: Received featured articles:', result);
      setArticles(result);
    } catch (err) {
      console.error('useFeaturedNews: Error fetching featured news:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch featured news';
      setError(errorMessage);

      // Set empty array on error
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useFeaturedNews: Effect triggered');
    fetchData();
  }, []);

  const refetch = async () => {
    console.log('useFeaturedNews: Refetching');
    await fetchData();
  };

  return {
    articles,
    loading,
    error,
    refetch,
  };
}
